import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";

export function PublicMemories() {
  const [data, setData] = useState(null);
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [sectionVisible, setSectionVisible] = useState(true);
  const [pageVisible, setPageVisible] = useState(true);
  const sectionRef = useRef(null);
  const resumeRef = useRef(null);
  const reducedMotion = useRef(
    typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches
  );

  useEffect(() => {
    api
      .get("/public/memories")
      .then((r) => setData(r.data))
      .catch(() => setData({ fotos: [], interval_seconds: 10 }));
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => setSectionVisible(e.isIntersecting), { threshold: 0.2 });
    if (sectionRef.current) obs.observe(sectionRef.current);
    const onVisibility = () => setPageVisible(!document.hidden);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      obs.disconnect();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);

  const fotos = data?.fotos || [];
  const intervalMs = Math.min(Math.max(data?.interval_seconds ?? 10, 3), 120) * 1000;

  const goTo = useCallback(
    (i) => setIndex((i + fotos.length) % fotos.length),
    [fotos.length]
  );

  const interact = (i) => {
    goTo(i);
    setPaused(true);
    clearTimeout(resumeRef.current);
    resumeRef.current = setTimeout(() => setPaused(false), intervalMs * 2);
  };

  const autoplayOn = !reducedMotion.current && !paused && sectionVisible && pageVisible && fotos.length > 1;
  useEffect(() => {
    if (!autoplayOn) return undefined;
    const t = setInterval(() => setIndex((i) => (i + 1) % fotos.length), intervalMs);
    return () => clearInterval(t);
  }, [autoplayOn, intervalMs, fotos.length]);

  useEffect(() => () => clearTimeout(resumeRef.current), []);

  if (!data || fotos.length === 0) return null;

  const current = fotos[index] || fotos[0];

  return (
    <section
      ref={sectionRef}
      className="bg-[#0A0A0A] py-20 lg:py-28"
      data-testid="memorias-section"
      aria-roledescription="carousel"
      aria-label="Memórias do IMJ"
    >
      <div className="mx-auto max-w-6xl px-4 sm:px-6">
        <div className="flex items-center gap-3">
          <span className="h-px w-10 bg-primary" />
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-primary">Acervo</span>
        </div>
        <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight text-white sm:text-4xl" data-testid="memorias-title">
          Memórias do IMJ
        </h2>

        <div
          className="group relative mt-10"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
        >
          <div
            className="relative aspect-[16/9] w-full cursor-pointer overflow-hidden border border-zinc-800 bg-zinc-900"
            onClick={() => document.getElementById("historia")?.scrollIntoView({ behavior: "smooth" })}
            role="link"
            aria-label={`Ver acervo histórico — ${current.titulo}`}
            title="Ver acervo histórico"
            data-testid="memorias-frame"
          >
            {fotos.map((f, i) => (
              <img
                key={f.id}
                src={f.imagem_url}
                alt={f.legenda || f.titulo}
                aria-hidden={i !== index}
                className={`absolute inset-0 h-full w-full object-cover ${
                  reducedMotion.current ? "" : "transition-opacity duration-700"
                } ${i === index ? "opacity-100" : "opacity-0"}`}
                data-testid={i === index ? "memorias-foto-ativa" : undefined}
              />
            ))}
            <div className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 to-transparent p-4 sm:p-6">
              <p className="font-heading text-base font-bold text-white sm:text-lg" data-testid="memorias-caption">
                {current.titulo}
                {current.ano ? ` · ${current.ano}` : ""}
              </p>
              {current.legenda && <p className="mt-1 text-xs text-zinc-300 sm:text-sm">{current.legenda}</p>}
            </div>
          </div>

          {fotos.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); interact(index - 1); }}
                aria-label="Foto anterior"
                className="absolute left-3 top-1/2 -translate-y-1/2 border border-zinc-700 bg-black/60 p-2 text-white transition-colors hover:border-primary hover:text-primary"
                data-testid="memorias-prev"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); interact(index + 1); }}
                aria-label="Próxima foto"
                className="absolute right-3 top-1/2 -translate-y-1/2 border border-zinc-700 bg-black/60 p-2 text-white transition-colors hover:border-primary hover:text-primary"
                data-testid="memorias-next"
              >
                <ChevronRight size={20} />
              </button>
              <div className="absolute bottom-3 right-4 flex gap-1.5" data-testid="memorias-dots">
                {fotos.map((f, i) => (
                  <button
                    key={f.id}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); interact(i); }}
                    aria-label={`Ir para foto ${i + 1}`}
                    className={`h-1.5 w-5 transition-colors ${i === index ? "bg-primary" : "bg-zinc-600 hover:bg-zinc-400"}`}
                    data-testid={`memorias-dot-${i}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </section>
  );
}
